import { useCallback, useEffect, useState } from 'react';
import { Boton, Centro, Grilla, GrillaEl, PilaV } from '@/components/atomos';
import SeccionPasosInstructivo from './SeccionPasosInstructivo';
import { Image, ImageProps, useBoolean, useMediaQuery } from '@chakra-ui/react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FACTORING } from '@/routes/AppRoutes';
import GiroDinero from '@/assets/img/giro_dinero.webp';
import RegistroClave from '@/assets/img/registro_clave.webp';
import SeleccionFacturas from '@/assets/img/seleccion_facturas.webp';

export type PasoInstructivo = {
  titulo: string;
  informacion: string;
  imagen: string;
};

const pasosInstructivoCrearNuevaOperacion: PasoInstructivo[] = [
  {
    titulo: 'Vincula tu cuenta de SII',
    informacion:
      'Al vincular tu cuenta con el SII podrás consolidar todas las facturas de tu negocio y acceder a financiamiento rápido y personalizado.',
    imagen: RegistroClave,
  },
  {
    titulo: 'Solicita tu financiamiento',
    informacion:
      'Selecciona aquellas facturas que deseas operar y llena los datos solicitados para realizar tu simulación.',
    imagen: SeleccionFacturas,
  },
  {
    titulo: 'Recibe el giro en menos de 24 horas',
    informacion:
      '¡Listo! Tu operación está en el flujo de evaluación. De salir aprobada recibirás el giro de tu dinero el mismo día.',
    imagen: GiroDinero,
  },
];

export type PropsInstructivoNuevaOp = {
  pasoActual: number;
  duracionSeccion: number; //ms
  pasos: PasoInstructivo[];
};

const duracionSeccion = 8000;

const propsImagenes: ImageProps = {
  aspectRatio: '1/1',
  objectFit: 'contain',
  maxH: 'full',
  w: 'full',
  position: 'absolute',
  alignSelf: 'center',
  animation: 'fadeIn 250ms forwards',
  filter: 'drop-shadow(0px 0px 3px #00000020)',
};

const InstructivoCrearNuevaOperacion = () => {
  const [montado, setMontado] = useBoolean(false);
  const [paso, setPaso] = useState(0);
  const [mobile] = useMediaQuery('(max-width: 768px)');

  const manejarIntructivo = useCallback(() => {
    // Luego de 5 segundos actualizo al paso siguiente o al inicial según sea requerido
    setTimeout(() => {
      setPaso((pasoActual) => {
        let nuevoEstado = pasoActual;

        if (pasoActual === pasosInstructivoCrearNuevaOperacion.length - 1) {
          nuevoEstado = 0;
        } else {
          nuevoEstado++;
        }

        return nuevoEstado;
      });
    }, duracionSeccion);

    // Siempre que actualizo el paso lo vuelvo a ejecutar
    // eslint-disable-next-line
  }, [paso]);

  useEffect(() => {
    if (!montado) return setMontado.on();
    manejarIntructivo();
  }, [manejarIntructivo, montado, setMontado]);

  return (
    <PilaV w='full' alignItems='start' spacing={6}>
      <Grilla templateColumns={mobile ? '1fr' : 'repeat(2, 1fr)'} gap='6'>
        <SeccionPasosInstructivo
          duracionSeccion={duracionSeccion}
          pasoActual={paso}
          pasos={pasosInstructivoCrearNuevaOperacion}
        />
        {!mobile ? (
          <GrillaEl
            colSpan={1}
            maxH='500px'
            bg='bGreen.100'
            as={Centro}
            rounded='md'
            shadow='md'
            position='relative'
            p='6'
          >
            <Image
              src={pasosInstructivoCrearNuevaOperacion[0]?.imagen}
              display={paso === 0 ? 'initial' : 'none'}
              {...propsImagenes}
            />
            <Image
              src={pasosInstructivoCrearNuevaOperacion[1]?.imagen}
              display={paso === 1 ? 'initial' : 'none'}
              {...propsImagenes}
            />
            <Image
              src={pasosInstructivoCrearNuevaOperacion[2]?.imagen}
              display={paso === 2 ? 'initial' : 'none'}
              {...propsImagenes}
            />
          </GrillaEl>
        ) : null}
      </Grilla>
      <Boton variant='action' size='lg' as={Link} to={FACTORING.OPERACIONES}>
        Crea una operación ahora <ChevronRight size={16} />
      </Boton>
    </PilaV>
  );
};

export default InstructivoCrearNuevaOperacion;
